import React, { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import './StatCoachModal.css';

export default function StatCoachModal({ isOpen, onClose, playerContext, initialMessage }) {
  const storageKey = playerContext?.playerId
    ? `analyst_history_${playerContext.playerId}_${playerContext?.statGroup || 'general'}`
    : null;

  const [messages, setMessages] = useState(() => {
    if (!storageKey) return [];
    try {
      const saved = sessionStorage.getItem(storageKey);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [usageCount, setUsageCount] = useState(null);
  const [usageLimit, setUsageLimit] = useState(null);
  const messagesEndRef = useRef(null);

  const greeting = playerContext?.playerName
    ? `Hi! I'm your Analyst. I've reviewed ${playerContext.playerName}'s stats and I'm here to help, whether that's interpreting numbers, suggesting drills, or just making sense of the season so far. What would you like to talk about?`
    : `Hi! I'm your Analyst. I'm here to help, whether that's interpreting numbers, suggesting drills, or just making sense of the season so far. What would you like to talk about?`;

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  useEffect(() => {
    if (!isOpen) {
      setError(null);
      setUsageCount(null);
      setUsageLimit(null);
    }
  }, [isOpen]);

  async function sendMessage(text, history) {
    const messageText = text || input.trim();
    if (!messageText || loading) return;
    setInput('');
    setError(null);

    const newHistory = [...(history || messages), { role: 'user', content: messageText }];
    setMessages(newHistory);
    if (storageKey) {
      try { sessionStorage.setItem(storageKey, JSON.stringify(newHistory)); } catch {}
    }
    setLoading(true);

    try {
      const res = await api.post('/coaching/chat', {
        message: messageText,
        conversationHistory: history || messages,
        playerContext,
        isAutoOpen: history !== undefined && history.length === 0,
      });
      setMessages(res.data.conversationHistory);
      if (storageKey) {
        try { sessionStorage.setItem(storageKey, JSON.stringify(res.data.conversationHistory)); } catch {}
      }
      if (res.data.usageCount !== null && res.data.usageLimit !== null) {
        setUsageCount(res.data.usageCount);
        setUsageLimit(res.data.usageLimit);
      }
    } catch (err) {
      if (err.response?.data?.usageLimitReached) {
        setError('limit');
      } else if (err.response?.data?.upgradeRequired) {
        setError('upgrade');
      } else {
        setError('Failed to get a response. Please try again.');
      }
      const filtered = messages.filter(m => m.content !== messageText || m.role !== 'user');
      setMessages(filtered);
      if (storageKey) {
        try { sessionStorage.setItem(storageKey, JSON.stringify(filtered)); } catch {}
      }
    } finally {
      setLoading(false);
    }
  }

  function handleKeyPress(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }

  function renderMarkdown(text) {
    if (!text) return null;
    const lines = text.split('\n');
    const elements = [];
    let key = 0;
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      const isBullet = trimmed.startsWith('- ') || trimmed.startsWith('* ');
      const content = isBullet ? trimmed.slice(2) : trimmed;
      const parts = content.split(/\*\*(.+?)\*\*/g);
      const rendered = parts.map((part, i) =>
        i % 2 === 1 ? <strong key={i}>{part}</strong> : part
      );
      if (isBullet) {
        elements.push(<li key={key++}>{rendered}</li>);
      } else {
        elements.push(<p key={key++} style={{ margin: '0 0 6px 0' }}>{rendered}</p>);
      }
    }
    const result = [];
    let bulletGroup = [];
    for (const el of elements) {
      if (el.type === 'li') {
        bulletGroup.push(el);
      } else {
        if (bulletGroup.length > 0) {
          result.push(<ul key={key++} style={{ margin: '4px 0 6px 16px', padding: 0 }}>{bulletGroup}</ul>);
          bulletGroup = [];
        }
        result.push(el);
      }
    }
    if (bulletGroup.length > 0) {
      result.push(<ul key={key++} style={{ margin: '4px 0 6px 16px', padding: 0 }}>{bulletGroup}</ul>);
    }
    return result;
  }

  if (!isOpen) return null;

  return (
    <div className="scm-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="scm-modal">
        <div className="scm-header">
          <div className="scm-header-left">
            <span className="scm-header-icon">💡</span>
            <div>
              <div className="scm-header-title">Ask the Analyst</div>
              <div className="scm-header-sub">{playerContext?.playerName} · {playerContext?.statGroup}</div>
            </div>
          </div>
          <button className="scm-close" onClick={onClose}>×</button>
        </div>

        {usageCount !== null && usageLimit !== null && usageCount < usageLimit && (
          <div className="scm-usage-strip">
            💡 {usageLimit - usageCount} of {usageLimit} Analyst messages remaining this month
            {usageLimit - usageCount <= 2 && (
              <a href="/membership"> · Upgrade for 200/month</a>
            )}
          </div>
        )}
        <div className="scm-messages">
          <div className="scm-message scm-message-assistant">
            {null}
            <div className="scm-bubble">{greeting}</div>
          </div>
          {messages.filter(m => m.role !== 'system').map((msg, i) => (
            <div key={i} className={`scm-message scm-message-${msg.role}`}>
              {null}
              <div className="scm-bubble">
                {msg.role === 'assistant' ? renderMarkdown(msg.content) : msg.content}
              </div>
            </div>
          ))}
          {loading && (
            <div className="scm-message scm-message-assistant">
              {null}
              <div className="scm-bubble scm-typing">
                <span /><span /><span />
              </div>
            </div>
          )}
          {error === 'limit' && (
            <div className="scm-upgrade-notice">
              🚫 You've used all {usageLimit} Analyst messages this month.
              <a href="/membership">Upgrade to Premium for 200 uses/month →</a>
            </div>
          )}
          {error === 'upgrade' && (
            <div className="scm-upgrade-notice">
              🔒 Ask the Analyst is a Pro feature.
              <a href="/membership">Upgrade to Pro →</a>
            </div>
          )}
          {error && error !== 'upgrade' && error !== 'limit' && (
            <div className="scm-error">{error}</div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="scm-quick-prompts">
          {(playerContext?.statGroup === 'Pitching'
            ? ['Drill for my command', 'Break down my WHIP', 'How is my K/BB?']
            : playerContext?.statGroup === 'Fielding'
            ? ['Position-specific drill', 'What does my FPCT mean?', 'How can I improve?']
            : ['Drill for my weakest stat', 'Explain my K/PA', 'How is my plate discipline?']
          ).map(prompt => (
            <button
              key={prompt}
              className="scm-quick-btn"
              onClick={() => sendMessage(prompt)}
              disabled={loading}
            >
              {prompt}
            </button>
          ))}
        </div>

        <div className="scm-input-row">
          <input
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={error === 'limit' ? 'Upgrade to continue...' : 'Ask about this stat...'}
            disabled={loading || error === 'limit'}
            className="scm-input"
          />
          <button
            onClick={() => sendMessage()}
            disabled={loading || !input.trim() || error === 'limit'}
            className="scm-send"
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
}
